import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Music2, Trash2, Search, Loader2, Play, Pause } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 100;
const resolveSrc = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : '');
const formatDuration = (ms) => {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Super-admin card: full-library view of every uploaded AI track across
 * every producer. Server-side paginated (100/page) + filtered. Inline
 * energy + genre editing, and delete.
 */
export function AdminAILibraryCard() {
  const [tracks, setTracks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [taxonomy, setTaxonomy] = useState({ energies: [], genres: [], genre_counts: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [energyFilter, setEnergyFilter] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [audioEl, setAudioEl] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  const loadTaxonomy = useCallback(async () => {
    try {
      const tax = await axios.get(`${API}/ai-library/taxonomy`);
      setTaxonomy(tax.data);
    } catch { /* silent */ }
  }, []);

  const fetchLib = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (q) params.q = q;
      if (energyFilter) params.energy = energyFilter;
      if (genreFilter) params.genre = genreFilter;
      const r = await axios.get(`${API}/admin/ai-library`, { params });
      setTracks(r.data.tracks || []);
      setTotal(r.data.total || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [page, q, energyFilter, genreFilter]);

  // Reset to first page whenever a filter/search changes.
  useEffect(() => { setPage(0); }, [q, energyFilter, genreFilter]);

  useEffect(() => { loadTaxonomy(); }, [loadTaxonomy]);

  // Debounced fetch (covers typing in the search box).
  useEffect(() => {
    const t = setTimeout(fetchLib, 250);
    return () => clearTimeout(t);
  }, [fetchLib]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  const updateTags = async (id, patch) => {
    try {
      const r = await axios.put(`${API}/admin/ai-library/${id}`, patch);
      setTracks((prev) => prev.map((t) => (t.id === id ? r.data : t)));
      loadTaxonomy();
      toast.success('Tags updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const deleteTrack = async (id) => {
    if (!window.confirm('Delete this track from the AI library? This can be recovered manually.')) return;
    try {
      await axios.delete(`${API}/admin/ai-library/${id}`);
      toast.success('Track deleted');
      fetchLib();
      loadTaxonomy();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const togglePreview = (t) => {
    if (playingId === t.id) {
      audioEl?.pause();
      setPlayingId(null);
      return;
    }
    const el = audioEl || new Audio();
    el.src = resolveSrc(t.url);
    el.play().catch(() => toast.error('Playback failed'));
    el.onended = () => setPlayingId(null);
    setAudioEl(el);
    setPlayingId(t.id);
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl" data-testid="admin-ai-library-card">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-[#39FF14]/15 ring-1 ring-[#39FF14]/40 flex items-center justify-center flex-shrink-0">
          <Music2 className="w-5 h-5 text-[#39FF14]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-extrabold tracking-tight">AI Music Library</h3>
          <p className="text-sm text-gray-400 mt-0.5">Every track producers have uploaded. Edit tags or remove tracks that don't fit.</p>
        </div>
        <span className="text-xs text-gray-500 self-center" data-testid="ai-library-total">{total} tracks</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, artist, producer…" className="pl-9 bg-black/40 border-white/10 h-9 text-sm" data-testid="ai-library-search" />
        </div>
        <select value={energyFilter} onChange={(e) => setEnergyFilter(e.target.value)} className="bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white" data-testid="ai-library-energy-filter">
          <option value="">All energies</option>
          {taxonomy.energies.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)} className="bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white" data-testid="ai-library-genre-filter">
          <option value="">All genres</option>
          {taxonomy.genres.map((g) => <option key={g} value={g}>{g.replace(/-/g, ' ')} ({taxonomy.genre_counts?.[g] ?? 0})</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 mx-auto text-white/30 animate-spin" /></div>
      ) : tracks.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl p-8 text-center bg-black/20">
          <Music2 className="w-8 h-8 mx-auto text-white/20 mb-2" />
          <p className="text-sm text-gray-400">No tracks match — invite a producer to upload some.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="ai-library-grid">
          {tracks.map((t) => (
            <div key={t.id} className="bg-white/[0.02] border border-white/10 rounded-xl px-3 py-3" data-testid={`ai-library-row-${t.id}`}>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => togglePreview(t)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center flex-shrink-0">
                  {playingId === t.id ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{t.title}</p>
                  <p className="text-xs text-gray-500 truncate">{t.artist || '—'} · {formatDuration(t.duration_ms)} · by {t.uploaded_by_name || '—'}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setEditingId(editingId === t.id ? null : t.id)} data-testid={`ai-library-edit-${t.id}`}>{editingId === t.id ? 'Close' : 'Edit'}</Button>
                <Button size="sm" variant="ghost" onClick={() => deleteTrack(t.id)} data-testid={`ai-library-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
              </div>
              {editingId === t.id ? (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="mb-2">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Energy</p>
                    <div className="flex flex-wrap gap-1.5">
                      {taxonomy.energies.map((e) => (
                        <button key={e} type="button" onClick={() => updateTags(t.id, { energy: e })}
                          className={`px-3 py-1 rounded-full text-xs font-semibold uppercase transition-all ${t.energy === e ? 'bg-[#FF007F] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Genres</p>
                    <div className="flex flex-wrap gap-1.5">
                      {taxonomy.genres.map((g) => (
                        <button key={g} type="button" onClick={() => {
                          const has = (t.genres || []).includes(g);
                          const nextGenres = has ? t.genres.filter((x) => x !== g) : [...(t.genres || []), g];
                          if (!nextGenres.length) return toast.error('At least one genre required');
                          updateTags(t.id, { genres: nextGenres });
                        }}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${(t.genres || []).includes(g) ? 'bg-[#39FF14] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                          {g.replace(/-/g, ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider bg-[#FF007F]/20 text-[#FF007F] rounded-full px-2 py-0.5">{t.energy}</span>
                  {(t.genres || []).map((g) => (
                    <span key={g} className="text-[10px] bg-white/5 text-gray-400 rounded-full px-2 py-0.5">{g.replace(/-/g, ' ')}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5" data-testid="ai-library-pagination">
          <span className="text-xs text-gray-500" data-testid="ai-library-range">
            Showing {rangeStart}–{rangeEnd} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="ai-library-prev"
              className="h-8 px-3 text-xs">Prev</Button>
            <span className="text-xs text-gray-400" data-testid="ai-library-page-indicator">Page {page + 1} / {totalPages}</span>
            <Button size="sm" variant="ghost" disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} data-testid="ai-library-next"
              className="h-8 px-3 text-xs">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAILibraryCard;
